import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Clock, Smile } from 'lucide-react';
import { Button } from './ui/button';
import { clinicInfo } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'About', href: '#about' },
    { name: 'Results', href: '#results' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:block bg-teal-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Phone size={14} />
              <span>{clinicInfo.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>{clinicInfo.hours.weekdays}</span>
            </div>
          </div>
          <div>
            <span>{clinicInfo.email}</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-md'
            : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <a href="#home" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <Smile className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-slate-800 tracking-tight">
                  {clinicInfo.name}
                </span>
                <p className="text-xs text-teal-600 font-medium -mt-0.5">
                  {clinicInfo.tagline}
                </p>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="px-4 py-2 text-slate-600 font-medium hover:text-teal-600 transition-colors duration-200 rounded-lg hover:bg-teal-50"
                >
                  {link.name}
                </a>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                asChild
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 btn-transition shadow-lg shadow-teal-600/20"
              >
                <a href="#appointment">Book Appointment</a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-96 border-t border-slate-100' : 'max-h-0'
          }`}
        >
          <nav className="bg-white px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-slate-600 font-medium hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              >
                {link.name}
              </a>
            ))}
            <div className="pt-3">
              <Button
                asChild
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold"
              >
                <a href="#appointment">Book Appointment</a>
              </Button>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
